import { Component, Input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';

const C_ALLOCATED = '#44C3C6';
const C_REMAIN = '#2E6BFF';
const C_TOTAL = '#FFD54D';

interface PieSegment {
  name: string;
  value: number;
  color: string;
  percentage: number;
  startAngle: number;
  endAngle: number;
}

@Component({
  selector: 'app-allocated-quotas-donut',
  imports: [CommonModule, MatCardModule, TranslateModule],
  templateUrl: './allocated-quotas-donut.html',
  styleUrl: './allocated-quotas-donut.css',
})
export class AllocatedQuotasDonutComponent {
  @Input() title: string = '';
  @Input() set totalVolume(value: number | null | undefined) {
    this._totalVolume.set(value ?? 0);
  }
  @Input() set totalAllocated(value: number | null | undefined) {
    this._totalAllocated.set(value ?? 0);
  }
  @Input() set remainingVolume(value: number | null | undefined) {
    this._remainingVolume.set(value ?? null);
  }

  private _totalVolume = signal<number>(0);
  private _totalAllocated = signal<number>(0);
  private _remainingVolume = signal<number | null>(null);

  segments = computed(() => {
    const total = Math.max(0, this._totalVolume());
    const allocated = Math.max(0, Math.min(this._totalAllocated(), total));
    const remaining = this._remainingVolume() !== null
      ? Math.max(0, this._remainingVolume()!)
      : Math.max(0, total - allocated);

    if (total === 0) {
      return [];
    }

    const allocatedPct = (allocated / total) * 100;
    const remainingPct = (remaining / total) * 100;

    const segments: PieSegment[] = [
      {
        name: 'Распределено',
        value: allocated,
        color: C_ALLOCATED,
        percentage: allocatedPct,
        startAngle: 0,
        endAngle: allocatedPct * 3.6
      },
      {
        name: 'Остаток',
        value: remaining,
        color: C_REMAIN,
        percentage: remainingPct,
        startAngle: allocatedPct * 3.6,
        endAngle: 360
      }
    ];

    return segments;
  });

  stats = computed(() => {
    const total = Math.max(0, this._totalVolume());
    const allocated = Math.max(0, Math.min(this._totalAllocated(), total));
    const remaining = this._remainingVolume() !== null
      ? Math.max(0, this._remainingVolume()!)
      : Math.max(0, total - allocated);

    return {
      total,
      allocated,
      remaining,
      totalTons: this.kgToTons(total),
      allocatedTons: this.kgToTons(allocated),
      remainingTons: this.kgToTons(remaining),
      allocatedPct: total > 0 ? Math.round((allocated / total) * 100) : 0,
      remainingPct: total > 0 ? Math.round((remaining / total) * 100) : 0
    };
  });

  readonly C_TOTAL = C_TOTAL;
  readonly C_ALLOCATED = C_ALLOCATED;
  readonly C_REMAIN = C_REMAIN;

  kgToTons(kg: number): string {
    if (kg === 0) return '0';
    const tons = kg / 1000;
    return tons.toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  }

  describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
    let arcAngle = endAngle - startAngle;
    if (arcAngle < 0) arcAngle += 360;

    // Для полного круга или почти полного (≥359.9°) рисуем две дуги
    if (arcAngle >= 359.9) {
      const mid = startAngle + 180;
      const midPoint = this.polarToCartesian(x, y, radius, mid);
      const start = this.polarToCartesian(x, y, radius, startAngle);
      const end = this.polarToCartesian(x, y, radius, endAngle);

      return [
        'M', start.x, start.y,
        'A', radius, radius, 0, 1, 1, midPoint.x, midPoint.y,
        'A', radius, radius, 0, 1, 1, end.x, end.y
      ].join(' ');
    }

    const start = this.polarToCartesian(x, y, radius, startAngle);
    const end = this.polarToCartesian(x, y, radius, endAngle);
    const largeArcFlag = arcAngle > 180 ? '1' : '0';

    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y
    ].join(' ');
  }

  private polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = angleInDegrees * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  }

  getLabelPosition(segment: PieSegment, radius: number, centerX: number, centerY: number): {x: number, y: number} {
    const midAngle = (segment.startAngle + segment.endAngle) / 2;
    const offset = 40;
    const r = radius + offset;
    const angleInRadians = midAngle * Math.PI / 180.0;

    return {
      x: centerX + (r * Math.cos(angleInRadians)),
      y: centerY + (r * Math.sin(angleInRadians))
    };
  }
}
